import { create } from 'zustand';

interface User {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  isPlatformAdmin?: boolean;
}

interface TenantContext {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: string;
}

interface AuthState {
  user: User | null;
  tenantContext: TenantContext | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  authToken: string | null;
  tokenType: string | null;
  
  login: (user: User, tenantContext?: TenantContext | null) => void;
  logout: () => void;
  setTenantContext: (context: TenantContext | null) => void;
  setAuthToken: (accessToken: string, tokenType: string, tenantContext?: TenantContext | null) => void;
  setHasHydrated: (state: boolean) => void;
}

type E2eAuthSeed = {
  user?: User | null;
  tenantContext?: TenantContext | null;
  isAuthenticated?: boolean;
};

const getE2eAuthSeed = (): E2eAuthSeed | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const seed = (window as { __E2E_AUTH__?: E2eAuthSeed }).__E2E_AUTH__;
  return seed ?? null;
};

const e2eSeed = getE2eAuthSeed();

export const useAuthStore = create<AuthState>()((set) => ({
  user: e2eSeed?.user ?? null,
  tenantContext: e2eSeed?.tenantContext ?? null,
  isAuthenticated: e2eSeed?.isAuthenticated ?? false,
  _hasHydrated: e2eSeed ? true : false,
  authToken: null,
  tokenType: null,

  login: (user, tenantContext) =>
    set({
      user,
      tenantContext: tenantContext ?? null,
      isAuthenticated: true,
      _hasHydrated: true,
    }),

  logout: () => {
    set({
      user: null,
      tenantContext: null,
      isAuthenticated: false,
      _hasHydrated: true,
      authToken: null,
      tokenType: null,
    });
  },

  setTenantContext: (context) =>
    set({ tenantContext: context }),

  setAuthToken: (accessToken, tokenType, tenantContext) =>
    set({
      authToken: accessToken,
      tokenType,
      tenantContext: tenantContext ?? null,
      isAuthenticated: true,
      _hasHydrated: true,
    }),

  setHasHydrated: (state) =>
    set({ _hasHydrated: state }),
}));
